import axios from 'axios';
import { formatAsISO } from 'Util/_HumanWritten/DateUtils';
import { serialiseDateTimeNoTz } from 'Util/AttributeUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import { AddUpdateVoucherDto } from 'Views/Components/_HumanWritten/AccountDetails/VoucherSection/GiftVouchersUtils';
import { buildUrl } from 'Util/FetchUtils';
import { VOUCHER_API, VOUCHER_DISCOUNT_API } from 'Constants';

export async function addVoucherOrPromotionToTransaction(
	transactionId: string,
	code: string,
	forCheckIn: boolean,
	eventBooking: boolean,
) {
	return axios.post(
		`${VOUCHER_DISCOUNT_API}/add-to-transaction?transactionId=${transactionId}&code=${encodeURIComponent(code)}&forCheckIn=${forCheckIn}&eventBooking=${eventBooking}`,
		{
			validateStatus: () => true,
		},
	);
}

/**
 * Removes a gift certificate from a transaction. Determines which gift certificate based on the supplied code and
 * determine the transaction based on the transaction id.
 *
 * Returns: The used value of the gift certificate which was removed from the transaction. If this did not occur then
 * the result will be 0.
 */
export async function removeGiftCertificateFromTransaction(
	transactionId: string,
	usageId: string,
	eventBooking: boolean,
	forCheckIn: boolean,
) {
	return axios.post(
		`${VOUCHER_API}/remove-from-transaction?transactionId=${transactionId}&usageId=${usageId}&eventBooking=${eventBooking}&forCheckIn=${forCheckIn}`,
		{
			validateStatus: () => true,
		},
	);
}

export function getUsersGiftVouchers(
	userId: string,
	skip: number,
	take: number,
	showExpiredOrUsed: boolean,
	search?: string,
) {
	const params: Record<string, string> = {
		userId: userId,
		skip: skip.toString(),
		take: take.toString(),
		showInvalid: showExpiredOrUsed ? 'true' : 'false',
	};
	if (search) {
		params.search = search;
	}
	return axios.get(buildUrl(`${VOUCHER_API}/get-users-gift-vouchers`, params)).then(x => x.data);
}

export interface PurchaseGiftCertificateDto {
	amount: number;
	isGift: boolean;
	recipientEmail?: string;
	message?: string;
	userId?: string;
	/** Only honoured for manager+ — the server falls back to the configured expiry for anyone else. */
	expiryDate?: Date;
}

export function purchaseGiftCertificate(dto: PurchaseGiftCertificateDto) {
	if (dto.expiryDate === undefined) {
		return axios.post(`${VOUCHER_API}/purchase-gift-voucher`, dto);
	}

	// Same normalisation as updateGiftVoucher: strip the time and send the local calendar date, so the
	// date the staff member picked is the date that is stored rather than one shifted by the timezone.
	const date = new Date(dto.expiryDate);
	const expiryDate = formatAsISO(new Date(date.getFullYear(), date.getMonth(), date.getDate()));

	return axios.post(`${VOUCHER_API}/purchase-gift-voucher`, { ...dto, expiryDate });
}

export function abandonVoucherTransaction(transactionId: string) {
	return axios.post(`${VOUCHER_API}/abandon-voucher-transaction?transactionId=${transactionId}`);
}

export interface VoucherListSummaryDto {
	id: string;
	code: string;
	description: string;
	initialValue: number;
	remainingValue: number;
	expiryDate: string;
	created: string;
	price: number | null;
	recipientEmail: string | null;
	hasMessage: boolean;
	message: string | null;
	disabled: boolean;
	status: string;
	voucherType: string;
	canConvertToRefund: boolean;
	userName: string | null;
	userEmail: string | null;
	userId: string | null;
	purchaserId: string | null;
	purchaserName: string | null;
	purchaserEmail: string | null;
	transactionHumanReadableId: string | null;
}

export interface VoucherFilterOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate?: boolean;
	typeFilters?: string[];
	statusFilters?: string[];
	orderBy?: string;
	orderByDescending?: boolean;
	skip?: number;
	take?: number;
}

export async function fetchFilteredVouchers(
	options: VoucherFilterOptions,
): Promise<{ data: VoucherListSummaryDto[]; count: number }> {
	const params = new URLSearchParams();

	if (stringNotEmpty(options.searchString)) {
		params.append('searchString', options.searchString!);
	}

	if (options.typeFilters && options.typeFilters.length > 0) {
		for (const type of options.typeFilters) {
			params.append('typeFilters', type);
		}
	}

	if (options.statusFilters && options.statusFilters.length > 0) {
		for (const status of options.statusFilters) {
			params.append('statusFilters', status);
		}
	}

	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}

	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}

	if (options.filterByCreatedDate !== undefined) {
		params.append('filterByCreatedDate', String(options.filterByCreatedDate));
	}

	if (options.orderBy !== undefined) {
		params.append('orderBy', options.orderBy);
	}

	if (options.orderByDescending !== undefined) {
		params.append('orderByDescending', String(options.orderByDescending));
	}

	if (options.skip !== undefined) {
		params.append('skip', options.skip.toString());
	}

	if (options.take !== undefined) {
		params.append('take', options.take.toString());
	}

	const response = await axios.get(`${VOUCHER_API}/fetch-filtered-vouchers`, { params });

	return {
		data: response.data.data as VoucherListSummaryDto[],
		count: response.data.count,
	};
}

export function claimGiftVoucher(code: string) {
	return axios.post(`${VOUCHER_API}/claim-gift-voucher`, { code });
}

export function toggleVoucherDisabled(voucherId: string) {
	return axios.post<{ disabled: boolean }>(`${VOUCHER_API}/toggle-voucher-disabled`, { voucherId });
}

/**
 * Converts the remaining value of a credit voucher into a manual (offline) refund. Voids the voucher
 * and creates a pending manual refund that staff settle through the booking's "Set to refunded" flow.
 */
export function convertVoucherToManualRefund(voucherId: string, note?: string) {
	return axios.post(`${VOUCHER_API}/convert-to-manual-refund`, { voucherId, note: note ?? '' });
}

export function convertRefundToCredit(refundId: string, note?: string) {
	return axios.post(`${VOUCHER_API}/convert-refund-to-credit`, { refundId, note: note ?? '' });
}

export function updateGiftVoucher(voucherDto: AddUpdateVoucherDto) {
	// Ensure we have access to date class methods
	const date = new Date(voucherDto.expiryDate);

	// Ensure the correct date format is provided to the endpoint
	const expiryDate = formatAsISO(new Date(date.getFullYear(), date.getMonth(), date.getDate()));

	const body = { ...voucherDto, expiryDate };

	return axios.post(`${VOUCHER_API}/update-gift-voucher`, body, {
		validateStatus: () => true,
	});
}
